import ReactGA from "react-ga4";
import { getAccessToken, getStoredPassportInfo } from "./token-store";

export type TInitGAOptions = {
  sourcePlatform?: string;
};

const DEFAULT_SOURCE_PLATFORM = "onboarding";

let configuredSourcePlatform = DEFAULT_SOURCE_PLATFORM;

/** @internal Test-only reset */
export function resetAnalyticsConfigForTests(): void {
  configuredSourcePlatform = DEFAULT_SOURCE_PLATFORM;
}

/**
 * 初始化 GA4
 * 在 App 入口 ClientProviders 调用一次；Dashboard 传 sourcePlatform: "dashboard"
 */
export function initGA(options?: TInitGAOptions): void {
  if (typeof window === "undefined" || !process.env.NEXT_PUBLIC_GA_ID) return;

  if (options?.sourcePlatform?.trim()) {
    configuredSourcePlatform = options.sourcePlatform.trim();
  }

  ReactGA.initialize(process.env.NEXT_PUBLIC_GA_ID, {
    gaOptions: {
      userId: getAnalyticsUserId() ?? undefined,
    },
  });

  syncGAUserContext();
}

/**
 * 获取当前用户 ID
 */
const getAnalyticsUserId = (): string | null => {
  const storedPassportInfo = getStoredPassportInfo();
  return storedPassportInfo?.passportId ?? null;
};

/**
 * 获取登录状态
 */
const getAnalyticsIsSignedIn = (): 0 | 1 => {
  return getAccessToken() ? 1 : 0;
};

function getSourcePlatform(): string {
  return configuredSourcePlatform;
}

/**
 * 同步用户信息和全局参数（在登录状态变更或路由跳转时调用）
 */
export function syncGAUserContext(): void {
  const userId = getAnalyticsUserId();

  if (userId) {
    ReactGA.set({ user_id: userId });
  }

  ReactGA.gtag("set", "user_properties", {
    source_platform: getSourcePlatform(),
    is_signed_in: getAnalyticsIsSignedIn(),
  });
}

/**
 * 上报 GA4 事件
 */
export function logGAEvent(
  eventName: string,
  params?: Record<string, unknown>,
): boolean {
  try {
    const MAX_EVENT_NAME_LENGTH = 40;
    const MAX_PARAM_VALUE_LENGTH = 100;

    const trimmedEventName = eventName.slice(0, MAX_EVENT_NAME_LENGTH);

    const processedParams: Record<string, unknown> = {
      current_route:
        typeof window !== "undefined" ? window.location.pathname : "",
      source_platform: getSourcePlatform(),
    };

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value && typeof value === "object" && !Array.isArray(value)) {
          Object.entries(value as Record<string, unknown>).forEach(
            ([nKey, nValue]) => {
              processedParams[`${key}_${nKey}`] =
                typeof nValue === "string"
                  ? nValue.slice(0, MAX_PARAM_VALUE_LENGTH)
                  : nValue;
            },
          );
        } else if (Array.isArray(value)) {
          processedParams[key] = value
            .join(",")
            .slice(0, MAX_PARAM_VALUE_LENGTH);
        } else {
          processedParams[key] =
            typeof value === "string"
              ? value.slice(0, MAX_PARAM_VALUE_LENGTH)
              : value;
        }
      });
    }

    ReactGA.event(trimmedEventName, processedParams);
    return true;
  } catch {
    return false;
  }
}
